import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { FaSortAmountDown, FaSortAmountUpAlt } from "react-icons/fa";
import privateAxios from "../../../../utils/axiosPrivate";
import { getManualSyncCurrentLogsUrl } from "../../../../utils/constants/apiEndPoints";
import {
  PAGE_INDEX,
  PAGE_SIZE
} from "../../../../utils/constants/appConstants";
import {
  noDataPresent,
  manualPaymentSync,
  commonErrMsg,
  SearchText
} from "../../../../locales/en";
import { showToast } from "../../../../utils/utils";
import { showToastType } from "../../../../utils/constants/appConstants";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import ErrorBoundary from "../../../error-boundary";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import DualPagination from "../../../common/DualPagination";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import Column from "./Column";
import "./PaymentSyncTable.scss";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return "";
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const PaymentSyncLogsTable = () => {
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(false);

  const columns = useMemo(() => Column(), []);

  const loadLogs = useCallback(
    async (page, size, query) => {
      try {
        setLoading(true);
        const params = {
          page: page,
          size: size,
          keyword: query,
          sortBy,
          order: sortOrder
        };
        const result = await privateAxios.get(getManualSyncCurrentLogsUrl, {
          params
        });

        if (result?.data?.data) {
          setData(result.data.data);
          if (result?.data?.pageInfo) {
            setTotalPages(result.data.pageInfo.totalPages || 0);
          } else {
            setTotalPages(1);
          }
        } else {
          setData([]);
          setTotalPages(0);
        }
      } catch {
        showToast(
          manualPaymentSync?.errors?.fetchLogsFailed || commonErrMsg?.item7,
          showToastType.error
        );
        setData([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [sortBy, sortOrder]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      gotoPage(0);
      setSearchQuery(trimmedQuery);
    }
  };

  useEffect(() => {
    const sortState = state.sortBy?.[0];
    if (sortState) {
      setSortBy(sortState.id);
      setSortOrder(sortState.desc ? "desc" : "asc");
    }
  }, [state.sortBy]);

  useEffect(() => {
    loadLogs(pageIndex, pageSize, searchQuery);
  }, [pageIndex, pageSize, searchQuery]);

  return (
    <ErrorBoundary fallback={<ListViewFallback />}>
      <div className="payment-sync-table-container">
        {/* Search Bar - Right Side */}
        <div className="table-search">
          <label className="search-label">{SearchText}</label>
          <input
            type="text"
            className="search-input"
            value={tempSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder={SearchText}
          />
        </div>

        {/* Table */}
        <div className="payment-sync-table-wrapper">
          <table
            className="mt-2 mb-3 payment-sync-table bgless_table"
            {...getTableProps()}
          >
            <thead ref={headerRef}>
              {headerGroups.map((headerGroup, i) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr
                    key={headerGroupKey || headerGroup.id || i}
                    {...headerGroupProps}
                  >
                    {headerGroup.headers.map((column, j) => {
                      const { key: colKey, ...colProps } =
                        column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th key={colKey || column.id || j} {...colProps}>
                          <span className="sort-icon-wrapper">
                            {renderSortIcon(column)}
                          </span>
                          <span className="header-text">
                            {column.render("Header")}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {loading && (
                <TableSkeleton rows={10} columns={visibleColumnCount} />
              )}
              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    {noDataPresent}
                  </td>
                </tr>
              )}
              {!loading &&
                data.length > 0 &&
                page.map((row, i) => {
                  prepareRow(row);
                  const { key: rowKey, ...rowProps } = row.getRowProps();
                  return (
                    <tr key={rowKey || row.id || i} {...rowProps}>
                      {row.cells.map((cell, j) => {
                        const { key: cellKey, ...cellProps } =
                          cell.getCellProps();
                        return (
                          <td
                            key={cellKey || cell.column.id || j}
                            {...cellProps}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.length > 0 && (
          <DualPagination
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            pageCount={pageCount}
            pageOptions={pageOptions}
            setPageSize={setPageSize}
            pageSize={pageSize}
            dataLength={data.length}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default PaymentSyncLogsTable;
